import { inject } from '@angular/core';
import type { MachineDatasheet } from '@indutrust/machine-model';
import { withFileUpload } from '@indutrust/shared-util';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { MachineParkApiClient } from '../machine-park-api-client.service';

type DatasheetContext = {
  contextFactoryId: number;
  contextMachineId: number;
};

type ContextStore = {
  contextFactoryId: () => number;
  contextMachineId: () => number;
};

/**
 * Modal-scoped store managing per-file upload/delete for
 * {@link MachinePark} datasheets.
 *
 * Provided directly in {@link FactoryFormMachineParkModal}'s `providers` so
 * each modal instance gets a fresh, isolated store.
 *
 * Before calling `commit()`, wire the machine context via `setContext()` —
 * typically from the modal's `seedForm` effect when `machine()` changes.
 *
 * @example
 * ```typescript
 * const uploadStore = inject(MachineParkDatasheetStore);
 * uploadStore.setContext(factoryId, machineId);
 * uploadStore.stageFiles([file]);
 * uploadStore.commit();
 * ```
 */
export const MachineParkDatasheetStore = signalStore(
  withState<DatasheetContext>({
    contextFactoryId: 0,
    contextMachineId: 0,
  }),
  withMethods((store) => ({
    /** Sets the machine context used by `commit()` for upload/delete URLs. */
    setContext(factoryId: number, machineId: number): void {
      patchState(store, {
        contextFactoryId: factoryId,
        contextMachineId: machineId,
      });
    },
  })),
  withFileUpload<MachineDatasheet>({
    upload: (store) => {
      const client = inject(MachineParkApiClient);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const ctx = store as ContextStore;
      return (file: File) =>
        client.uploadDatasheet(
          ctx.contextFactoryId(),
          ctx.contextMachineId(),
          file
        );
    },
    delete: (store) => {
      const client = inject(MachineParkApiClient);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const ctx = store as ContextStore;
      return (datasheetId: string) =>
        client.deleteDatasheet(
          ctx.contextFactoryId(),
          ctx.contextMachineId(),
          Number(datasheetId)
        );
    },
  })
);
